/**
 * BroadcastForm - Main form for creating/editing broadcasts
 * Extracted from BroadcastsTab.jsx
 */
import { motion } from "framer-motion";
import { Trophy, Image, Wand2, Upload, MapPin } from "lucide-react";
import { ImageUpload } from "../ImageUpload";
import { TeamFormSection, YouTubeStreamSection, LiveStatsSection, AutoScheduleSection } from "./index";

export const BroadcastForm = ({
  formData,
  setFormData,
  editingId,
  channels,
  events,
  teams,
  onSubmit,
  onCancel,
  saving,
  newLink,
  setNewLink,
  addLink,
  handleExtractTeamColor,
  extractingColor,
  selectTeam,
  setTeamTBD,
  handleGenerateThumbnail,
  generatingThumbnail,
  handleThumbnailUpload,
  uploadingThumbnail,
  thumbnailInputRef
}) => {

  return (
    <motion.form 
      initial={{ opacity: 0, y: -10 }} 
      animate={{ opacity: 1, y: 0 }} 
      onSubmit={onSubmit} 
      className="p-6 bg-zinc-900/50 border border-white/10 space-y-6"
      data-testid="broadcast-form"
    >
      <h3 className="font-secondary text-lg font-bold uppercase text-white">
        {editingId ? "Edit Game" : "Schedule New Game"}
      </h3>
      
      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Title *</label>
          <input 
            type="text" 
            value={formData.title} 
            onChange={(e) => setFormData({ ...formData, title: e.target.value })} 
            required 
            placeholder="Game Title" 
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Sport *</label>
          <select 
            value={formData.sport} 
            onChange={(e) => setFormData({ ...formData, sport: e.target.value })} 
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          >
            {["Football", "Basketball", "Boys Basketball", "Girls Basketball", "Mens Basketball", "Womens Basketball", "Soccer", "Baseball", "Softball", "Volleyball", "Wrestling", "Tennis", "Golf", "Track & Field", "Swimming", "Lacrosse", "Hockey"].map(s => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">
            Channel {!formData.youtube_url && !formData.vcloud_url && !formData.m3u8_url && '*'}
            {(formData.youtube_url || formData.vcloud_url || formData.m3u8_url) && <span className="text-zinc-600 font-normal ml-2">(Optional when video URL set)</span>}
          </label>
          <select 
            value={formData.channel_id} 
            onChange={(e) => setFormData({ ...formData, channel_id: e.target.value })} 
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm"
          >
            <option value="">-- No Channel --</option>
            {channels.filter(ch => !ch.is_conference_network || ch.visible !== false).map((ch) => <option key={ch.channel_id} value={ch.channel_id}>{ch.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Date</label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
              <input 
                type="checkbox" 
                checked={formData.date_tbd} 
                onChange={(e) => setFormData({ ...formData, date_tbd: e.target.checked, scheduled_date: e.target.checked ? "" : formData.scheduled_date })} 
                className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-orange-500 focus:ring-orange-500"
              />
              <span>Date TBD</span>
            </label>
            {!formData.date_tbd && (
              <input 
                type="date" 
                value={formData.scheduled_date} 
                onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })} 
                required 
                className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
              />
            )}
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Time</label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
              <input 
                type="checkbox" 
                checked={formData.time_tbd || formData.date_tbd} 
                disabled={formData.date_tbd}
                onChange={(e) => setFormData({ ...formData, time_tbd: e.target.checked, scheduled_time: e.target.checked ? "" : formData.scheduled_time })} 
                className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-orange-500 focus:ring-orange-500 disabled:opacity-50"
              />
              <span className={formData.date_tbd ? "opacity-50" : ""}>Time TBD</span>
            </label>
            {!formData.time_tbd && !formData.date_tbd ? (
              <input 
                type="time" 
                value={formData.scheduled_time} 
                onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })} 
                required 
                className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
              />
            ) : (
              <div className="w-full bg-zinc-800/50 border border-white/5 text-zinc-500 px-3 py-2 text-sm">TBD</div>
            )}
          </div>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">End Time <span className="text-zinc-600 normal-case">(Optional)</span></label>
          {!formData.time_tbd && !formData.date_tbd ? (
            <input 
              type="time" 
              value={formData.end_time || ""} 
              onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} 
              className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
              data-testid="end-time-input"
            />
          ) : (
            <div className="w-full bg-zinc-800/50 border border-white/5 text-zinc-500 px-3 py-2 text-sm">—</div>
          )}
          <p className="text-zinc-600 text-[10px] mt-1">Auto-creates a time block on 24/7 channels</p>
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Description</label>
          <input 
            type="text" 
            value={formData.description} 
            onChange={(e) => setFormData({ ...formData, description: e.target.value })} 
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Custom URL <span className="text-zinc-600 normal-case">(Optional)</span></label>
          <input 
            type="url" 
            value={formData.custom_url || ""} 
            onChange={(e) => setFormData({ ...formData, custom_url: e.target.value })} 
            placeholder="https://..." 
            className="w-full bg-zinc-800 border border-white/10 text-white px-3 py-2 text-sm" 
            data-testid="custom-url-input"
          />
          <p className="text-zinc-600 text-[10px] mt-1">Users clicking this game on the schedule will be redirected here</p>
        </div>
      </div>

      {/* Event Assignment */}
      {events.length > 0 && (
        <div className="p-4 bg-zinc-800/50 border border-white/5">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-yellow-500" />
            <h4 className="font-secondary text-sm font-bold uppercase text-zinc-400">Event Assignment</h4>
          </div>
          <select 
            value={formData.event_id} 
            onChange={(e) => setFormData({ ...formData, event_id: e.target.value })} 
            className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm"
            data-testid="event-select"
          >
            <option value="">-- No Event (Standalone Game) --</option>
            {events.map(event => (
              <option key={event.id} value={event.id}>
                {event.name} ({event.sport}) - {event.start_date}
              </option>
            ))}
          </select>
          <p className="text-xs text-zinc-600 mt-2">Optionally assign this broadcast to a tournament, showcase, or multi-game event</p>
        </div>
      )}

      {/* Teams */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <TeamFormSection
          label="Home Team"
          team={formData.home_team}
          onChange={(team) => setFormData({ ...formData, home_team: team })}
          teams={teams}
          onSelectTeam={(id) => id === 'TBD' ? setTeamTBD(true) : selectTeam(id, true)}
          onExtractColor={() => handleExtractTeamColor(true)}
          extractingColor={extractingColor.home}
          testIdPrefix="home"
          allowTBD={true}
          maxprepsLink={formData.home_maxpreps_link}
          onMaxprepsLinkChange={(val) => setFormData({ ...formData, home_maxpreps_link: val })}
        />
        <TeamFormSection
          label="Away Team"
          team={formData.away_team}
          onChange={(team) => setFormData({ ...formData, away_team: team })}
          teams={teams}
          onSelectTeam={(id) => id === 'TBD' ? setTeamTBD(false) : selectTeam(id, false)}
          onExtractColor={() => handleExtractTeamColor(false)}
          extractingColor={extractingColor.away}
          testIdPrefix="away"
          allowTBD={true}
          maxprepsLink={formData.away_maxpreps_link}
          onMaxprepsLinkChange={(val) => setFormData({ ...formData, away_maxpreps_link: val })}
        />
      </div>

      {/* Thumbnail */}
      <div className="p-4 bg-zinc-800/50 border border-white/5">
        <div className="flex items-center gap-2 mb-3">
          <Image className="w-4 h-4 text-purple-500" />
          <h4 className="font-secondary text-sm font-bold uppercase text-zinc-400">Thumbnail</h4>
        </div>
        
        <ImageUpload
          value={formData.thumbnail_url}
          onChange={(url) => setFormData({ ...formData, thumbnail_url: url })}
          label=""
          placeholder="Thumbnail URL"
          previewSize="lg"
          id="broadcast-thumbnail"
        />
        
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={handleGenerateThumbnail}
            disabled={generatingThumbnail || !formData.home_team.logo_url || !formData.away_team.logo_url}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold rounded disabled:opacity-50"
          >
            <Wand2 className={`w-4 h-4 ${generatingThumbnail ? 'animate-spin' : ''}`} />
            {generatingThumbnail ? 'Generating...' : 'Auto-Generate'}
          </button>
          <label className="flex items-center gap-2 px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-white text-xs font-bold rounded cursor-pointer">
            <Upload className="w-4 h-4" />
            {uploadingThumbnail ? 'Uploading...' : 'Upload'}
            <input 
              type="file" 
              ref={thumbnailInputRef}
              accept="image/*" 
              onChange={handleThumbnailUpload} 
              className="hidden" 
            />
          </label>
        </div>
        <p className="text-[10px] text-zinc-600 mt-2">Add team logos first to enable auto-generation</p>
      </div>

      {/* Location & Tickets */}
      <div className="p-4 bg-zinc-800/50 border border-white/5">
        <div className="flex items-center gap-2 mb-3">
          <MapPin className="w-4 h-4 text-blue-500" />
          <h4 className="font-secondary text-sm font-bold uppercase text-zinc-400">Location & Tickets</h4>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Venue Name</label>
            <input 
              type="text" 
              value={formData.location || ''} 
              onChange={(e) => setFormData({ ...formData, location: e.target.value })} 
              placeholder="e.g., Memorial Stadium"
              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" 
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Ticket Link</label>
            <input 
              type="url" 
              value={formData.ticket_link || ''} 
              onChange={(e) => setFormData({ ...formData, ticket_link: e.target.value })} 
              placeholder="https://tickets.example.com"
              className="w-full bg-zinc-900 border border-white/10 text-white px-3 py-2 text-sm" 
            />
          </div>
        </div>
        
        <div className="mt-4">
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-500 mb-2">Location Photo</label>
          <ImageUpload
            value={formData.location_image || ''}
            onChange={(url) => setFormData({ ...formData, location_image: url })}
            label=""
            placeholder="Stadium/venue image URL"
            previewSize="md"
            id="location-image"
          />
          <p className="text-[10px] text-zinc-600 mt-2">Optional: Add a photo of the venue</p>
        </div>
      </div>

      {/* Video Sources (YouTube / VCloud / HLS) */}
      <YouTubeStreamSection formData={formData} setFormData={setFormData} />
      
      {/* Live Stats */}
      <LiveStatsSection 
        formData={formData} 
        setFormData={setFormData}
        newLink={newLink}
        setNewLink={setNewLink}
        addLink={addLink}
      />
      
      {/* Auto-Schedule / Recording */}
      <AutoScheduleSection formData={formData} setFormData={setFormData} />

      {/* Form Actions */}
      <div className="flex items-center gap-3 pt-4 border-t border-white/10">
        <button 
          type="submit" 
          disabled={saving} 
          className="px-6 py-2 bg-white text-black font-bold text-xs uppercase hover:bg-zinc-200 disabled:opacity-50"
        >
          {saving ? "Saving..." : editingId ? "Update Game" : "Schedule Game"}
        </button>
        <button 
          type="button" 
          onClick={onCancel} 
          className="text-zinc-500 text-sm hover:text-white"
        >
          Cancel
        </button>
      </div>
    </motion.form>
  );
};

export default BroadcastForm;
